import React, { useMemo } from "react";

import Alert from "veneer/Alert";
import { ChartData } from "veneer/Chart";

import { MEGABYTE } from "cortex/ui/src/constant/app";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { name as AppName } from "cortex/ui/src/redux-store/applications/selectors";

import ResourceLineChart from "./ResourceLineChart";
import { ChartPoint } from "../model";

interface MemoryChartProps {
  applications: Record<number, Application>;
  data: ReadonlyArray<ChartPoint>;
}

interface MemoryUsagePoint {
  x: number;
  y: number;
}

const BS_APP_ID = -1;
const PLAYER_APP_ID = -2;
const GPU_APP_ID = -3;

function getFakeAppName(appId: number): string | null {
  switch (appId) {
    case BS_APP_ID:
      return "Black Screen";
    case PLAYER_APP_ID:
      return "Player";
    case GPU_APP_ID:
      return "GPU";
    default:
      return null;
  }
}

function getAppName(
  applications: MemoryChartProps["applications"],
  appId: string,
): string {
  const id = parseInt(appId, 10);
  const fakeAppName = getFakeAppName(id);
  if (fakeAppName) {
    return fakeAppName;
  }

  const app = applications[id];
  return app ? AppName(app) : appId;
}

function getChartData({
  applications,
  data,
}: MemoryChartProps): ChartData<"line"> {
  const dataByAppId = data.reduce<Record<number, MemoryUsagePoint[]>>(
    (acc, point) => {
      const [timestamp, appId, bytes] = point;

      if (!acc[appId]) {
        acc[appId] = [];
      }

      acc[appId].push({
        x: timestamp,
        y: bytes / MEGABYTE,
      });

      return acc;
    },
    {},
  );

  return {
    datasets: Object.entries(dataByAppId).map(([appId, points]) => ({
      borderWidth: 1,
      data: points,
      label: getAppName(applications, appId),
      pointRadius: 0,
      tension: 0.4,
    })),
  };
}

function formatTooltipValue(label: string, value: number): string {
  return `${label}: ${value.toFixed(2)} MB`;
}

export default function MemoryChart({ applications, data }: MemoryChartProps) {
  const chartData = useMemo(
    () => getChartData({ applications, data }),
    [applications, data],
  );
  const timestamps = useMemo(
    () => data.map(([timestamp]) => timestamp),
    [data],
  );

  if (data.length === 0) {
    return (
      <Alert dense severity="info">
        Memory usage data is only available for devices running Cortex 2.0 or
        later. If your device is running an earlier version of Cortex, please
        upgrade to the latest version to view memory usage data.
      </Alert>
    );
  }

  return (
    <ResourceLineChart
      data={chartData}
      formatTooltipValue={formatTooltipValue}
      timestamps={timestamps}
    />
  );
}
